import { BUSINESS_TYPE } from '@/constants';
import { fetchProfiles } from '@/core/fetch-profiles';
import { getState, setState, subscribe } from '@/store';
import type { Profile, ProfilesState } from '@/types';
import { PL_LOG_GROUPS, logger } from '@/utils';
import { LOG_LEVEL } from '@b2x/shared';
import { SeamlessConnection } from '@seamless/store';
import type { AnyAction, Dispatch, Reducer } from 'redux';
import type { ProfileContext, SDSProfileDispatchers } from './types';

export const LEGACY_SDS_PROFILE_CONNECTION = 'B2XCORE_PROFILE';
const ACTION_SET_STATE = 'SET_STATE';

export class SDSProfileConnection extends SeamlessConnection<SDSProfileDispatchers, ProfilesState> {
  initialState: ProfilesState;

  constructor() {
    super(LEGACY_SDS_PROFILE_CONNECTION);
    this.initialState = getState();
  }

  getReducer(): Reducer {
    return (state: ProfilesState, action: AnyAction) => {
      switch (action.type) {
        case this.getActionType(ACTION_SET_STATE): {
          const currentState: ProfilesState = { ...state, ...action.payload };
          return currentState;
        }
        default:
          return state;
      }
    };
  }

  getPublicDispatchers(): SDSProfileDispatchers {
    logger('\n\n\n 🚨 Using deprecated dispatchers!', {
      args: [
        '\n\n ⛔ ProfileConnection is scheduled for removal! Please upgrade to OneContext! ',
        '\n\n ⛔ Migrate to OneContext: https://pages.git.i.mercedes-benz.com/dh-io-seamless/documentation/v7/guidelines/one-context/connections/profile-connection.html \n\n\n',
      ],
      level: LOG_LEVEL.WARN,
      group: PL_LOG_GROUPS.LEGACY,
    });

    return {
      setBusinessType: (options) => {
        logger('\n\n\n 🚨 Using deprecated setBusinessType dispatcher!', {
          args: [
            '\n\n ⛔ ProfileConnection is scheduled for removal! Please upgrade to OneContext! ',
            '\n\n ⛔ Migrate to OneContext: https://pages.git.i.mercedes-benz.com/dh-io-seamless/documentation/v7/guidelines/one-context/connections/profile-connection.html \n\n\n',
          ],
          level: LOG_LEVEL.WARN,
          group: PL_LOG_GROUPS.LEGACY,
        });

        return () => {
          this.setContextAction({ businessType: options.businessType });
        };
      },

      setActiveProfile: (options) => {
        logger('\n\n\n 🚨 Using deprecated setActiveProfile dispatcher!', {
          args: [
            '\n\n ⛔ ProfileConnection is scheduled for removal! Please upgrade to OneContext! ',
            '\n\n ⛔ Migrate to OneContext: https://pages.git.i.mercedes-benz.com/dh-io-seamless/documentation/v7/guidelines/one-context/connections/profile-connection.html \n\n\n',
          ],
          level: LOG_LEVEL.WARN,
          group: PL_LOG_GROUPS.LEGACY,
        });

        return () => {
          this.setContextAction({ meId: options.profileID });
        };
      },

      setStateProfile: (profileStateParams) => {
        return () => {
          logger('\n\n\n 🚨 Using deprecated setStateProfile dispatcher!', {
            args: [
              '\n\n ⛔ ProfileConnection is scheduled for removal! Please upgrade to OneContext! ',
              '\n\n ⛔ Migrate to OneContext: https://pages.git.i.mercedes-benz.com/dh-io-seamless/documentation/v7/guidelines/one-context/connections/profile-connection.html \n\n\n',
            ],
            level: LOG_LEVEL.WARN,
            group: PL_LOG_GROUPS.LEGACY,
          });
          setState(profileStateParams);
        };
      },

      setRefetchProfiles: (options) => {
        return async () => {
          logger('\n\n\n 🚨 Using deprecated setRefetchProfiles dispatcher!', {
            args: [
              '\n\n ⛔ ProfileConnection is scheduled for removal! Please upgrade to OneContext! ',
              '\n\n ⛔ Migrate to OneContext: https://pages.git.i.mercedes-benz.com/dh-io-seamless/documentation/v7/guidelines/one-context/connections/profile-connection.html \n\n\n',
            ],
            level: LOG_LEVEL.WARN,
            group: PL_LOG_GROUPS.LEGACY,
          });

          const { profiles, meta } = await fetchProfiles();
          const activeProfile = defineActiveProfile(options, profiles);

          setState({
            activeProfile,
            availableProfiles: profiles,
            meta,
          });

          logger('Refetch Profiles', {
            args: [options],
            level: LOG_LEVEL.WARN,
            group: PL_LOG_GROUPS.LEGACY,
          });
        };
      },
    };
  }

  private setContextAction(profileContext: ProfileContext) {
    const { businessType, meId } = profileContext;

    const { availableProfiles, activeProfile, meta } = getState();

    const updatedActiveProfile = defineActiveProfile({ businessType, meId }, availableProfiles);

    logger('Context changed:', {
      args: [`New: ${updatedActiveProfile.businessType} |`, `Old: ${activeProfile.businessType}`],
      group: PL_LOG_GROUPS.LEGACY,
      level: LOG_LEVEL.INFO,
    });

    setState({
      activeProfile: updatedActiveProfile,
      availableProfiles,
      meta,
    });
  }

  onBeforeRegister(dispatch: Dispatch): void | Promise<void> {
    subscribe((state: ProfilesState) => {
      dispatch(this.getAction(ACTION_SET_STATE, state));
    });
  }
}

function defineActiveProfile(condition: ProfileContext, profiles: Profile[]): Profile {
  const { businessType, meId = null } = condition;

  const firstProfile = profiles.at(0) as Profile;

  // "error" profile
  if (!firstProfile.id) {
    return firstProfile;
  }

  // B2C as active profile
  if (businessType === BUSINESS_TYPE.B2C) {
    return profiles.find((profile) => profile.businessType === BUSINESS_TYPE.B2C) ?? firstProfile;
  }

  // B2B as active profile
  return resolveActiveB2bProfile(profiles, meId) ?? firstProfile;
}

function resolveActiveB2bProfile(profiles: Profile[], profileId: string | null): Profile | undefined {
  if (profileId) {
    const foundProfileById = profiles.find((profile) => profile.id === profileId);

    if (foundProfileById) {
      return foundProfileById;
    }

    const foundProfileByMeId = profiles.find((profile) => profile.meId === profileId);

    if (foundProfileByMeId) {
      return foundProfileByMeId;
    }
  }

  return profiles.find((profile) => profile.businessType === BUSINESS_TYPE.B2B);
}
